import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setProfile } from "../../../redux/slice/profileSlice";
import { setSession } from "../../../redux/slice/authSlice";
import { verifyAuthToken } from "../../../services/authService";
import { clearEtsyMarkers } from "../../../services/etsyService";
import { claimGift, giftWelcomePath } from "../../../services/giftService";
import { loadUserData } from "../../../util/navigationUtils";
import { clearShopifySessionCookies } from "../../../util/sessionCookies";
import { setTitle } from "../../../helpers/MetaTag";

/**
 * /gift?t=... — where the button in a gift email lands.
 *
 * Someone bought this person a coloring book on Shopify. The emailed link is
 * their sign-in: the API checks it, signs them in to the account for their
 * email (creating it the first time), and this page sends them to the gift
 * page. A finished gift goes to My Books instead.
 *
 * Public, on the bare logo-only layout, for the same reason as /etsy: the
 * normal front layout bounces signed-in visitors, which would race the
 * navigation this page does itself.
 */
const GiftLink = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  const [error, setError] = useState(null);
  const started = useRef(false);

  // The app checks whatever sign-in this browser already holds as it starts.
  // Claiming before that check finishes goes wrong two ways: the app merges
  // identical in-flight requests, so our check of the NEW token would get the
  // old token's answer; and an old token that fails gets deleted from storage
  // when its answer lands, taking the new one with it. So wait for it — but
  // never forever.
  const sessionStatus = useSelector((state) => state?.authProvider?.session?.status);
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setWaitedLongEnough(true), 4000);
    return () => clearTimeout(timer);
  }, []);
  const startupCheckDone = sessionStatus !== "loading" || waitedLongEnough;

  useEffect(() => {
    setTitle("Your gift | MemoryDoodle");
  }, []);

  useEffect(() => {
    if (!startupCheckDone) return;
    // Once per page load: a second claim would race the first one's navigation.
    if (started.current) return;
    started.current = true;

    if (!token) {
      setError("This link isn't complete. Please use the button in your gift email.");
      return;
    }

    (async () => {
      const result = await claimGift(token);
      if (!result || !result.ok) {
        setError((result && result.message) || "Something went wrong. Please try again.");
        return;
      }

      // A gift recipient is an ordinary customer; nothing from an Etsy build
      // in this browser applies to them.
      clearEtsyMarkers();
      // Otherwise the next reload puts back whoever last signed in through
      // Shopify on this browser — often the person who bought the gift.
      clearShopifySessionCookies();
      localStorage.setItem("auth_token", result.accessToken);

      const body = await verifyAuthToken();
      if (!body || body.status !== 200 || !body.data) {
        setError("We couldn't sign you in. Please try again in a moment.");
        return;
      }
      const authUser = body.data;
      const accessToken = localStorage.getItem("auth_token");
      dispatch(setProfile({ ...authUser, access_token: accessToken }));
      dispatch(setSession({ user: { ...authUser, access_token: accessToken }, status: "authorized", error: null }));

      try {
        await loadUserData(authUser._id, dispatch, false);
      } catch (loadError) {
        // The gift page loads what it needs itself; My Books retries its own load.
        console.error("Loading user data after gift sign-in failed:", loadError);
      }

      // The welcome page sends a recipient who already started books on to the hub.
      navigate(result.gift.submitted ? "/dashboard" : giftWelcomePath(result.gift.giftId), { replace: true });
    })();
  }, [token, dispatch, navigate, startupCheckDone]);

  return (
    <div className="flex justify-center px-4 py-12 sm:py-20">
      <div className="w-full max-w-md rounded-2xl border border-md-pink-light-1 bg-white p-6 text-center shadow-sm sm:p-8">
        {error ? (
          <>
            <h1 className="text-2xl font-semibold text-md-dark">We couldn't open your gift</h1>
            <p role="alert" className="mt-3 text-neutral-600">
              {error}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-md-dark">Opening your gift…</h1>
            <div className="mx-auto mt-6 h-2 w-40 animate-pulse rounded-full bg-md-pink-light-2" />
          </>
        )}
      </div>
    </div>
  );
};

export default GiftLink;
